import React, { useState, useEffect, useRef } from 'react';
import API_BASE_URL from '../config/api';
import {
  Box,
  Grid,
  Typography,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  Avatar,
  Chip,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Menu,
  MenuItem,
  Badge,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  Input,
  Checkbox,
  ListItemIcon
} from '@mui/material';
import {
  Send as SendIcon,
  Person as PersonIcon,
  Psychology as PsychologyIcon,
  ExpandMore as ExpandMoreIcon,
  Article as ArticleIcon,
  Share as ShareIcon,
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Chat as ChatIcon,
  Group as GroupIcon,
  Search as SearchIcon,
  Delete as DeleteIcon,
  OpenInNew as OpenInNewIcon,
  Forum as ForumIcon,
  MenuBook as MenuBookIcon
} from '@mui/icons-material';
import { fetchWithAuth, loadStateFromDatabase, syncStateToDatabase } from '../utils/auth';
import { jwtDecode } from 'jwt-decode';
import { useNavigate } from 'react-router-dom';
import { useInteractiveTutorial } from '../utils/InteractiveTutorialContext';
import { getButtonStyles, BUTTON_COLORS } from '../config/buttonStyles';
import { useTheme } from '../utils/ThemeContext';
import '../assets/css/futuristic-modern.css';

const Collaboration = () => {
  const navigate = useNavigate();
  const { completeStep, isActive: isTutorialActive, getCurrentStep, checkUnlockConditions } = useInteractiveTutorial();
  const { isDarkMode } = useTheme();
  // eslint-disable-next-line no-unused-vars
  const [activeTab, setActiveTab] = useState(0);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareType, setShareType] = useState('ai_conversation'); // 'ai_conversation', 'article'
  const [shareableItems, setShareableItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [filterType, setFilterType] = useState('all');
  const [conversationSearch, setConversationSearch] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [bookmarkedMessages, setBookmarkedMessages] = useState(new Set());
  const [conversationHistory, setConversationHistory] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [totalUnreadCount, setTotalUnreadCount] = useState(0);
  const [newChatDialogOpen, setNewChatDialogOpen] = useState(false);
  const [colleagueSearchQuery, setColleagueSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchingColleagues, setSearchingColleagues] = useState(false);
  const [conversationMenuAnchor, setConversationMenuAnchor] = useState(null);
  const [selectedConversationForMenu, setSelectedConversationForMenu] = useState(null);
  const [articleModalOpen, setArticleModalOpen] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [dialogWidth, setDialogWidth] = useState(1200); // Default width in pixels (slightly smaller to leave space for guides)
  const [isResizing, setIsResizing] = useState(false);
  const [attachmentMenuAnchor, setAttachmentMenuAnchor] = useState(null);
  const [databaseStateLoaded, setDatabaseStateLoaded] = useState(false);
  const messagesEndRef = useRef(null);

  // Function to truncate message to first 4 words
  const truncateMessage = (message, maxWords = 4) => {
    if (!message) return 'No messages yet';
    const words = message.trim().split(/\s+/);
    if (words.length <= maxWords) return message;
    return words.slice(0, maxWords).join(' ') + '...';
  };

  // Load users and conversation history on mount
  useEffect(() => {
    loadUsers();
    loadConversationHistory();
    
    // Check unlock conditions when page loads
    if (checkUnlockConditions) {
      checkUnlockConditions();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Restore last conversation after users are loaded AND database state is loaded
  useEffect(() => {
    // DISABLED: Auto-restoration disabled - user must manually select who to chat with
    // if (users.length > 0 && databaseStateLoaded) {
    //   restoreLastConversation();
    // }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users, databaseStateLoaded]);

  // Load conversations whenever selectedUser changes
  useEffect(() => {
    if (selectedUser) {
      loadConversations();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUser]);

  // Event-driven refresh on page focus (instead of polling)
  // Refresh messages when user returns to tab
  useEffect(() => {
    const handleFocus = () => {
      if (!document.hidden) {
        loadConversationHistory();
        if (selectedUser) {
          loadConversations();
        }
      }
    };
    
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleFocus);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleFocus);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUser]);

  // Visibility change already handled by focus event above
  // (Combined to avoid duplicate listeners)

  // Helper to get user-specific localStorage key
  const getUserConversationKey = () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return null;
      
      const decoded = jwtDecode(token);
      const userEmail = decoded.sub || null;
      
      return userEmail ? `lastCollaborationConversation_${userEmail}` : null;
    } catch (error) {
      console.error('Error getting user conversation key:', error);
      return null;
    }
  };

  // Save current conversation to localStorage when it changes (user-specific)
  useEffect(() => {
    if (selectedUser) {
      const conversationKey = getUserConversationKey();
      if (conversationKey) {
        const conversationData = {
          userId: selectedUser.id,
          userName: selectedUser.name,
          timestamp: Date.now()
        };
        localStorage.setItem(conversationKey, JSON.stringify(conversationData));
      }
    }
  }, [selectedUser]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Close article modal when leaving article_detail_view step
  useEffect(() => {
    if (isTutorialActive && getCurrentStep) {
      const currentStep = getCurrentStep();
      // If we're moving away from article_detail_view step and modal is open, close it
      if (currentStep?.id !== 'article_detail_view' && articleModalOpen) {
        const previousStepWasArticleView = sessionStorage.getItem('lastTutorialStep') === 'article_detail_view';
        if (previousStepWasArticleView) {
          handleCloseArticleModal();
        }
      }
      // Track the current step
      sessionStorage.setItem('lastTutorialStep', currentStep?.id || '');
    }
  }, [isTutorialActive, getCurrentStep, articleModalOpen]);

  // Load state from database on mount (cross-device sync)
  useEffect(() => {
    const loadInitialState = async () => {
      try {
        await loadStateFromDatabase();
        setDatabaseStateLoaded(true);
      } catch (error) {
        console.error('Error loading collaboration state from database:', error);
        setDatabaseStateLoaded(true); // Set to true even on error so we don't block
      }
    };
    
    loadInitialState();
  }, []);

  // Periodic sync to database (every 5 minutes)
  useEffect(() => {
    const syncInterval = setInterval(() => {
      syncStateToDatabase().catch(error => {
        console.error('Periodic collaboration sync error:', error);
      });
    }, 5 * 60 * 1000); // 5 minutes
    
    return () => clearInterval(syncInterval);
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await fetchWithAuth('/api/collaboration/users');
      const data = await response.json();
      
      if (data.error) {
        setError(data.error);
      } else {
        setUsers(data.users || []);
      }
    } catch (err) {
      setError('Failed to load users');
      console.error('Error loading users:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadConversationHistory = async () => {
    try {
      const response = await fetchWithAuth('/api/collaboration/conversation-history');
      const data = await response.json();
      
      if (data.error) {
        setError(data.error);
      } else {
        setConversationHistory(data.conversations || []);
        // Calculate total unread count
        const totalUnread = (data.conversations || []).reduce((sum, conv) => sum + (conv.unread_count || 0), 0);
        setTotalUnreadCount(totalUnread);
      }
    } catch (err) {
      setError('Failed to load conversation history');
      console.error('Error loading conversation history:', err);
    }
  };

  const searchColleagues = async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    try {
      setSearchingColleagues(true);
      const response = await fetchWithAuth(`/api/collaboration/search-users?q=${encodeURIComponent(query)}`);
      const data = await response.json();
      
      if (data.error) {
        setError(data.error);
      } else {
        setSearchResults(data.users || []);
      }
    } catch (err) {
      setError('Failed to search colleagues');
      console.error('Error searching colleagues:', err);
    } finally {
      setSearchingColleagues(false);
    }
  };

  const handleNewChatClick = () => {
    setNewChatDialogOpen(true);
    setColleagueSearchQuery('');
    setSearchResults([]);
    
    // Complete tutorial step if active
    if (isTutorialActive && getCurrentStep && getCurrentStep()?.id === 'new_chat_button') {
      completeStep('new_chat_button');
    }
  };

  const handleStartChat = (user) => {
    setSelectedUser(user);
    setNewChatDialogOpen(false);
    setColleagueSearchQuery('');
    setSearchResults([]);
    
    // Complete tutorial step if active
    if (isTutorialActive && getCurrentStep && getCurrentStep()?.id === 'select_user') {
      completeStep('select_user');
    }
    
    // Messages will be loaded automatically by useEffect when selectedUser changes
  };

  const handleColleagueSearchChange = (e) => {
    const query = e.target.value;
    setColleagueSearchQuery(query);
    
    // Debounce the search
    const timeoutId = setTimeout(() => {
      searchColleagues(query);
    }, 300);
    
    return () => clearTimeout(timeoutId);
  };

  const restoreLastConversation = async () => {
    try {
      // Get user-specific conversation key
      const conversationKey = getUserConversationKey();
      if (!conversationKey) {
        return; // No valid user
      }
      
      const lastConversation = localStorage.getItem(conversationKey);
      
      if (lastConversation) {
        const { userId, timestamp } = JSON.parse(lastConversation);
        
        // Only restore if conversation is recent (within last 7 days)
        const sevenDaysInMs = 7 * 24 * 60 * 60 * 1000;
        const isRecent = timestamp && (Date.now() - timestamp < sevenDaysInMs);
        
        if (!isRecent) {
          localStorage.removeItem(conversationKey);
          return;
        }
        
        // Check if the user still exists in our users list
        const user = users.find(u => u.id === userId);
        if (user) {
          setSelectedUser(user);
          // Messages will be loaded automatically by useEffect when selectedUser changes
        } else {
          // Clear the saved conversation if user no longer exists
          localStorage.removeItem(conversationKey);
        }
      }
    } catch (err) {
      console.error('Error restoring last conversation:', err);
    }
  };

  const handleConversationMenuOpen = (event, conversation) => {
    event.preventDefault();
    event.stopPropagation();
    setConversationMenuAnchor(event.currentTarget);
    setSelectedConversationForMenu(conversation);
  };

  const handleConversationMenuClose = () => {
    setConversationMenuAnchor(null);
    setSelectedConversationForMenu(null);
  };

  const handleAttachmentMenuOpen = (event) => {
    setAttachmentMenuAnchor(event.currentTarget);
  };

  const handleAttachmentMenuClose = () => {
    setAttachmentMenuAnchor(null);
  };

  const handleShareFromAttachmentMenu = (type) => {
    handleAttachmentMenuClose();
    handleShareDialogOpen(type);
    
    // Complete tutorial step if active and sharing article
    if (type === 'article' && isTutorialActive && getCurrentStep && getCurrentStep()?.id === 'share_article') {
      completeStep('share_article');
    }
  };

  const handleArticleClick = (article) => {
    // Use requestAnimationFrame for smoother opening
    requestAnimationFrame(() => {
      console.log('🔍 Article clicked:', article);
      console.log('🔍 Article URL:', article.url);
      console.log('🔍 Article keys:', Object.keys(article));
      console.log('🔍 All article fields:', article);
      console.log('🔍 Article annotations:', article.annotations);
      console.log('🔍 Article annotations count:', article.annotations?.length || 0);
      setSelectedArticle(article);
      setArticleModalOpen(true);
      
      // Complete tutorial step if active
      if (isTutorialActive && getCurrentStep && getCurrentStep()?.id === 'view_shared_content') {
        completeStep('view_shared_content');
      }
    });
  };

  const handleCloseArticleModal = () => {
    setArticleModalOpen(false);
    setSelectedArticle(null);
  };

  // Resize functionality
  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsResizing(true);
  };

  const handleMouseMove = (e) => {
    if (!isResizing) return;
    
    const newWidth = e.clientX;
    const minWidth = 400;
    const maxWidth = window.innerWidth * 0.9;
    
    if (newWidth >= minWidth && newWidth <= maxWidth) {
      setDialogWidth(newWidth);
    }
  };

  const handleMouseUp = () => {
    setIsResizing(false);
  };

  // Add event listeners for resize
  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isResizing]);

  const handleDeleteConversation = async () => {
    if (!selectedConversationForMenu) return;
    
    try {
      const response = await fetchWithAuth(`/api/collaboration/delete-conversation/${selectedConversationForMenu.user_id}`, {
        method: 'DELETE'
      });
      
      const data = await response.json();
      
      if (data.error) {
        setError(data.error);
      } else {
        // Remove from conversation history
        setConversationHistory(prev => prev.filter(conv => conv.user_id !== selectedConversationForMenu.user_id));
        
        // If this was the currently selected conversation, clear it
        if (selectedUser?.id === selectedConversationForMenu.user_id) {
          setSelectedUser(null);
          setMessages([]);
          setNewMessage('');
          const conversationKey = getUserConversationKey();
          if (conversationKey) {
            localStorage.removeItem(conversationKey);
          }
        }
        
        // Refresh conversation history
        loadConversationHistory();
        
        // Complete tutorial step if active
        if (isTutorialActive && getCurrentStep && getCurrentStep()?.id === 'conversation_menu') {
          completeStep('conversation_menu');
        }
      }
      
      handleConversationMenuClose();
    } catch (err) {
      setError('Failed to delete conversation');
      console.error('Error deleting conversation:', err);
    }
  };

  const loadConversations = async () => {
    if (!selectedUser) return;
    
    try {
      const response = await fetchWithAuth(`/api/collaboration/conversations/${selectedUser.id}`);
      const data = await response.json();
      
      if (data.error) {
        setError(data.error);
      } else {
        setMessages(data.messages || []);
      }
    } catch (err) {
      setError('Failed to load conversation');
      console.error('Error loading conversation:', err);
    }
  };

  const loadShareableItems = async (type) => {
    try {
      let endpoint = '';
      switch (type) {
        case 'ai_conversation':
          endpoint = `${API_BASE_URL}/api/ai/conversation-summaries`;
          break;
        case 'article':
          endpoint = `${API_BASE_URL}/api/collaboration/shareable-articles`;
          break;
        default:
          return;
      }
      
      const response = await fetchWithAuth(endpoint);
      const data = await response.json();
      
      if (data.error) {
        setError(data.error);
      } else {
        console.log('🔍 Loaded shareable items:', data);
        console.log('🔍 Items structure:', data.items);
        console.log('🔍 Items type:', typeof data.items);
        console.log('🔍 Items length:', data.items?.length);
        
        if (data.items && data.items.length > 0) {
          console.log('🔍 First item structure:', data.items[0]);
          console.log('🔍 First item keys:', Object.keys(data.items[0]));
          
          if (data.items[0].articles) {
            console.log('🔍 First query articles:', data.items[0].articles);
            console.log('🔍 Article IDs:', data.items[0].articles.map(a => a.id));
          } else {
            console.log('🔍 No articles property found in first item');
          }
        } else {
          console.log('🔍 No items found or items is empty');
        }
        setShareableItems(data.items || []);
      }
    } catch (err) {
      setError('Failed to load shareable items');
      console.error('Error loading shareable items:', err);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedUser || sending) return;
    
    try {
      setSending(true);
      const response = await fetchWithAuth('/api/collaboration/send-message', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          recipient_id: selectedUser.id,
          message: newMessage.trim(),
          message_type: 'text'
        })
      });
      
      const data = await response.json();
      
      if (data.error) {
        setError(data.error);
      } else {
        setMessages(prev => [...prev, data.message]);
        setNewMessage('');
        // Refresh conversation history to update unread counts
        loadConversationHistory();
      }
    } catch (err) {
      setError('Failed to send message');
      console.error('Error sending message:', err);
    } finally {
      setSending(false);
    }
  };

  const shareItems = async () => {
    if (selectedItems.length === 0 || !selectedUser) return;
    
    try {
      setSending(true);
      
      // Transform selected IDs into full item objects
      let itemsToShare = [];
      
      if (shareType === 'article') {
        // Find the full article details from shareableItems
        shareableItems.forEach(queryGroup => {
          if (queryGroup.articles) {
            queryGroup.articles.forEach(article => {
              if (selectedItems.includes(article.id)) {
                const articleToShare = {
                  type: 'article',
                  id: article.id,
                  annotationId: article.annotationId, // Preserve the annotation ID
                  title: article.title || 'No title',
                  authors: article.authors || 'Unknown Authors',
                  journal: article.journal || 'Unknown Journal',
                  year: article.year || 'Unknown Year',
                  abstract: article.abstract || 'No abstract available',
                  description: article.description || '',
                  url: article.link || article.url || ''
                };
                
                itemsToShare.push(articleToShare);
              }
            });
          }
        });
      } else if (shareType === 'ai_conversation') {
        // Find the full conversation details from shareableItems
        shareableItems.forEach((conversation, index) => {
          const conversationId = conversation.id || conversation._id || `conv-${index}`;
          if (selectedItems.includes(conversationId)) {
            const conversationToShare = {
              type: 'ai_conversation',
              id: conversationId,
              title: conversation.title || conversation.preview || 'Untitled Conversation',
              preview: conversation.preview || conversation.title || '',
              content: conversation.preview || conversation.title || 'AI Conversation shared with you',
              date: conversation.date || conversation.created_at || conversation.createdAt || new Date().toISOString(),
              message_count: conversation.message_count || conversation.messages?.length || 0
            };
            
            itemsToShare.push(conversationToShare);
          }
        });
      }
      
      const response = await fetchWithAuth('/api/collaboration/share-items', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          recipient_id: selectedUser.id,
          items: itemsToShare,
          share_type: shareType,
          message: newMessage.trim() || `Shared ${shareType.replace('_', ' ').replace(/\bai\b/gi, 'AI')} with you`
        })
      });
      
      const data = await response.json();
      
      if (data.error) {
        setError(data.error);
      } else {
        setMessages(prev => [...prev, data.message]);
        setShareDialogOpen(false);
        setSelectedItems([]);
        setNewMessage('');
        
        // Emit event to update collaboration badge in navigation
        window.dispatchEvent(new CustomEvent('collaborationMessageSent'));
        
        // Complete tutorial step if active
        if (isTutorialActive && getCurrentStep && getCurrentStep()?.id === 'click_share_button') {
          completeStep('click_share_button');
        }
      }
    } catch (err) {
      setError('Failed to share items');
      console.error('Error sharing items:', err);
    } finally {
      setSending(false);
    }
  };


  // eslint-disable-next-line no-unused-vars
  const toggleBookmark = async (messageId) => {
    try {
      const response = await fetchWithAuth(`/api/collaboration/toggle-bookmark/${messageId}`, {
        method: 'POST'
      });
      
      const data = await response.json();
      
      if (data.error) {
        setError(data.error);
      } else {
        setBookmarkedMessages(prev => {
          const newSet = new Set(prev);
          if (newSet.has(messageId)) {
            newSet.delete(messageId);
          } else {
            newSet.add(messageId);
          }
          return newSet;
        });
      }
    } catch (err) {
      setError('Failed to toggle bookmark');
      console.error('Error toggling bookmark:', err);
    }
  };

  const handleShareDialogOpen = (type) => {
    console.log('🔍 Opening share dialog for type:', type);
    setShareType(type);
    setShareDialogOpen(true);
    loadShareableItems(type);
  };

  // eslint-disable-next-line no-unused-vars
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // eslint-disable-next-line no-unused-vars
  const filteredUsers = users.filter(user =>
    user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.role.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.institution.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.department.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.specialization.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredMessages = messages.filter(message =>
    message.content.toLowerCase().includes(conversationSearch.toLowerCase()) ||
    (message.shared_items && message.shared_items.some(item => 
      item.title?.toLowerCase().includes(conversationSearch.toLowerCase())
    ))
  );

  // eslint-disable-next-line no-unused-vars
  const getMessageIcon = (message) => {
    if (message.shared_items && message.shared_items.length > 0) {
      const item = message.shared_items[0];
      switch (item.type) {
        case 'ai_conversation':
          return <PsychologyIcon />;
        case 'article':
          return <ArticleIcon />;
        default:
          return <ShareIcon />;
      }
    }
    return <ChatIcon />;
  };

  const renderSharedItem = (item, message) => {
    switch (item.type) {
      case 'ai_conversation':
        return (
          <Card sx={{ 
            mt: 1, 
            bgcolor: 'var(--surface-2)',
            border: '1px solid var(--glass-border)',
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--card-shadow)',
            cursor: 'pointer',
            '&:hover': {
              transform: 'translateY(-1px)',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
              border: '1px solid var(--neon-cyan)'
            },
            transition: 'all 0.2s ease-in-out'
          }}
          data-tutorial="shared-content"
          onClick={() => {
            // Check if current user is the sender or recipient
            const isCurrentUserSender = message.sender_id === 'current_user';
            
            if (isCurrentUserSender) {
              // Current user is the sender - redirect to regular conversation
              console.log('🔍 Current user is sender, redirecting to regular conversation:', item.id);
              navigate(`/ai-assistant?conversation=${item.id}`);
            } else {
              // Current user is the recipient - redirect to shared conversation
              console.log('🔍 Current user is recipient, redirecting to shared conversation:', item.id);
              navigate(`/ai-assistant?shared=${item.id}`);
            }
          }}
          >
            <CardContent sx={{ p: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PsychologyIcon sx={{ color: 'var(--neon-cyan)', fontSize: '1.2rem' }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ 
                    color: 'var(--text-primary)',
                    fontWeight: 'bold',
                    lineHeight: 1.3,
                    display: '-webkit-box',
                    WebkitLineClamp: 1,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}>
                    {item.title || item.preview || 'AI Conversation'}
                  </Typography>
                  <Typography variant="caption" sx={{ 
                    color: 'var(--text-muted)',
                    display: 'block',
                    mt: 0.25
                  }}>
                    {item.message_count ? `${item.message_count} messages` : 'AI Chat'} • Click to view
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        );
      case 'article':
        return (
          <Card sx={{ 
            mt: 1, 
            bgcolor: 'var(--surface-2)',
            border: '1px solid var(--glass-border)',
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--card-shadow)',
            cursor: 'pointer',
            '&:hover': {
              transform: 'translateY(-1px)',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
              border: '1px solid var(--success)'
            },
            transition: 'all 0.2s ease-in-out'
          }}
          data-tutorial="shared-content"
          onClick={() => handleArticleClick(item)}
          >
            <CardContent sx={{ p: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ArticleIcon sx={{ color: 'var(--success)', fontSize: '1.2rem' }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ 
                    color: 'var(--text-primary)',
                    fontWeight: 'bold',
                    lineHeight: 1.3,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}>
                    {item.title}
                  </Typography>
                  <Typography variant="caption" sx={{ 
                    color: 'var(--text-muted)',
                    display: 'block',
                    mt: 0.25
                  }}>
                    {item.journal} • {item.year} • Click to view
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        );
      default:
        return null;
    }
  };

  return (
    <Box sx={{ 
      height: 'calc(100vh - 90px)', 
      padding: '1rem 2.7rem 2rem 1rem', 
      background: 'var(--surface)', 
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      maxHeight: 'calc(100vh - 90px)',
      minHeight: 'calc(100vh - 90px)'
    }}>

      {error && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 2, 
            flexShrink: 0,
            backgroundColor: '#ffebee !important',
            color: '#000000 !important',
            '& .MuiAlert-icon': {
              color: '#c62828 !important'
            },
            '& .MuiAlert-message': {
              color: '#000000 !important'
            },
            '& *': {
              color: '#000000 !important'
            },
            border: '1px solid #ef5350'
          }} 
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      <Grid container spacing={2} style={{ height: 'calc(100vh - 140px)', overflow: 'hidden', margin: '0 3rem 0 0', flex: 1 }}>
        {/* Left Panel - Users List */}
        <Grid item xs={12} md={4} style={{ height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <div className="modern-card" style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }} data-tutorial="conversation-history-section">
            {/* Header */}
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
              <Typography variant="h6" className="text-neon" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ForumIcon style={{ fontSize: '1.2rem', color: '#6c3082' }} />
                Conversation History
              </Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <Button 
                  size="small" 
                  onClick={handleNewChatClick}
                  className="btn-modern"
                  data-tutorial="new-chat-button"
                  startIcon={<AddIcon />}
                  sx={getButtonStyles(false, isDarkMode, 'generic')}
                >
                  New Chat
                </Button>
              </Box>
            </Box>

            {/* Search */}
            <Box sx={{ mb: 2, flexShrink: 0 }}>
              <TextField
                size="small"
                placeholder="Search conversations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-tutorial="conversation-search"
                InputProps={{
                  startAdornment: <SearchIcon sx={{ color: 'var(--text-secondary)', mr: 1 }} />
                }}
                sx={{ 
                  width: '100%',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 'var(--radius-md)',
                    backgroundColor: 'var(--surface-2)',
                    '& fieldset': {
                      borderColor: 'var(--glass-border)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'var(--neon-cyan)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'var(--neon-cyan)',
                    },
                  }
                }}
              />
            </Box>

            {/* Conversation History List */}
            <Box sx={{ 
              flex: 1, 
              overflow: 'auto', 
              minHeight: 0,
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                background: 'var(--glass-border)',
                borderRadius: '3px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: 'var(--neon-cyan)',
              },
            }}>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                  <div className="loading-spinner-modern"></div>
                </Box>
              ) : (
                <List dense>
                  {conversationHistory
                    .filter(conv => 
                      searchQuery === '' || 
                      conv.participant_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                      conv.last_message?.toLowerCase().includes(searchQuery.toLowerCase())
                    )
                    .map((conversation) => (
                    <ListItem 
                      key={conversation.user_id} 
                      className="modern-card"
                      data-tutorial="conversation-item"
                      sx={{ 
                        py: 1, 
                        mb: 1,
                        cursor: 'pointer',
                        bgcolor: selectedUser?.id === conversation.user_id ? 'rgba(0, 212, 255, 0.1)' : 'transparent',
                        border: selectedUser?.id === conversation.user_id ? '1px solid var(--neon-cyan)' : '1px solid transparent',
                        '&:hover': { 
                          backgroundColor: 'rgba(255, 255, 255, 0.05)',
                          transform: 'translateY(-1px)'
                        },
                        transition: 'all 0.2s ease-in-out'
                      }}
                      onClick={(e) => {
                        // Safari-compatible click handler
                        e.preventDefault();
                        e.stopPropagation();
                        
                        const user = users.find(u => u.id === conversation.user_id);
                        if (user) {
                          // Use requestAnimationFrame for Safari compatibility
                          requestAnimationFrame(() => {
                            setSelectedUser(user);
                            // Messages will be loaded automatically by useEffect when selectedUser changes
                          });
                        }
                      }}
                      secondaryAction={
                        <IconButton
                          edge="end"
                          onClick={(e) => handleConversationMenuOpen(e, conversation)}
                          size="small"
                          data-tutorial="conversation-menu-button"
                          sx={{
                            opacity: 1,
                            backgroundColor: 'rgba(108, 48, 130, 0.3) !important',
                            border: '1px solid rgba(108, 48, 130, 0.5) !important',
                            color: '#6c3082 !important',
                            minWidth: '24px',
                            width: '24px',
                            height: '24px',
                            borderRadius: 'var(--radius-sm)',
                            '&:hover': {
                              opacity: '1 !important',
                              backgroundColor: 'rgba(108, 48, 130, 0.5) !important',
                              border: '1px solid rgba(108, 48, 130, 0.7) !important',
                              transform: 'scale(1.05)',
                              boxShadow: '0 2px 8px rgba(108, 48, 130, 0.4)',
                            },
                            transition: 'all 0.2s ease-in-out',
                          }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      }
                    >
                      <ListItemIcon sx={{ minWidth: 48 }}>
                        <Avatar sx={{ 
                          bgcolor: 'var(--neon-cyan)',
                          color: 'white',
                          width: 32,
                          height: 32,
                          fontSize: '0.875rem'
                        }}>
                          {conversation.participant_name.charAt(0).toUpperCase()}
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="body2" noWrap style={{ color: 'var(--text-primary)' }}>
                            {conversation.participant_name}
                          </Typography>
                        }
                        secondary={
                          <div>
                            <Typography variant="caption" style={{ color: 'var(--text-muted)' }}>
                              {truncateMessage(conversation.last_message)}
                            </Typography>
                            <br />
                            <Typography variant="caption" style={{ color: 'var(--text-muted)' }}>
                              {conversation.last_message_time ? new Date(conversation.last_message_time).toLocaleDateString() : ''}
                            </Typography>
                          </div>
                        }
                      />
                      {conversation.unread_count > 0 && (
                        <Badge 
                          badgeContent={conversation.unread_count} 
                          color="error"
                          sx={{
                            '& .MuiBadge-badge': {
                              bgcolor: 'var(--neon-cyan)',
                              color: 'white',
                              fontWeight: 'bold'
                            }
                          }}
                        />
                      )}
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>

            {/* Conversation Menu */}
            <Menu
              anchorEl={conversationMenuAnchor}
              open={Boolean(conversationMenuAnchor)}
              onClose={handleConversationMenuClose}
              PaperProps={{
                sx: {
                  backgroundColor: 'var(--surface-2)',
                  border: '1px solid var(--glass-border)',
                  borderRadius: 'var(--radius-md)',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                  backdropFilter: 'blur(10px)',
                  '& .MuiMenuItem-root': {
                    color: 'var(--text-primary)',
                    padding: '0.75rem 1rem',
                    backgroundColor: 'transparent !important',
                    '&:hover': {
                      backgroundColor: 'transparent !important',
                    },
                    '&:active': {
                      backgroundColor: 'transparent !important',
                    },
                  },
                }
              }}
            >
              <MenuItem 
                onClick={handleDeleteConversation}
                sx={{
                  color: 'var(--text-primary)',
                  padding: '0.75rem 1rem',
                  borderRadius: 'var(--radius-sm)',
                  margin: '0.25rem',
                  backgroundColor: 'transparent !important',
                  border: '1px solid transparent',
                    '&:hover': {
                    backgroundColor: `${BUTTON_COLORS.hoverFill} !important`,
                    borderColor: BUTTON_COLORS.borderHover,
                    transform: 'none',
                  },
                  transition: 'background-color 0.2s ease-in-out, border-color 0.2s ease-in-out',
                }}
              >
                <DeleteIcon fontSize="small" sx={{ mr: 1, color: 'var(--text-primary)' }} />
                Delete Conversation
              </MenuItem>
            </Menu>

            {/* Attachment Menu */}
            <Menu
              anchorEl={attachmentMenuAnchor}
              open={Boolean(attachmentMenuAnchor)}
              onClose={handleAttachmentMenuClose}
              PaperProps={{
                sx: {
                  backgroundColor: 'var(--surface-2)',
                  border: '1px solid var(--glass-border)',
                  borderRadius: 'var(--radius-md)',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                  backdropFilter: 'blur(10px)',
                  minWidth: '200px',
                  '& .MuiMenuItem-root': {
                    color: 'var(--text-primary)',
                    padding: '0.75rem 1rem',
                    backgroundColor: 'transparent !important',
                    '&:hover': {
                      backgroundColor: 'transparent !important',
                    },
                    '&:active': {
                      backgroundColor: 'transparent !important',
                    },
                  },
                }
              }}
            >
              <MenuItem 
                onClick={() => handleShareFromAttachmentMenu('ai_conversation')}
                data-tutorial="share-ai-conversation"
                sx={{
                  color: 'var(--text-primary)',
                  padding: '0.75rem 1rem',
                  borderRadius: 'var(--radius-sm)',
                  margin: '0.25rem',
                  backgroundColor: 'transparent !important',
                  border: '1px solid transparent',
                    '&:hover': {
                    backgroundColor: `${BUTTON_COLORS.hoverFill} !important`,
                    borderColor: BUTTON_COLORS.borderHover,
                    transform: 'none',
                  },
                  transition: 'background-color 0.2s ease-in-out, border-color 0.2s ease-in-out',
                }}
              >
                Share AI Conversation
              </MenuItem>
              <MenuItem 
                onClick={() => handleShareFromAttachmentMenu('article')}
                data-tutorial="share-article"
                sx={{
                  color: 'var(--text-primary)',
                  padding: '0.75rem 1rem',
                  borderRadius: 'var(--radius-sm)',
                  margin: '0.25rem',
                  backgroundColor: 'transparent !important',
                  border: '1px solid transparent',
                    '&:hover': {
                    backgroundColor: `${BUTTON_COLORS.hoverFill} !important`,
                    borderColor: BUTTON_COLORS.borderHover,
                    transform: 'none',
                  },
                  transition: 'background-color 0.2s ease-in-out, border-color 0.2s ease-in-out',
                }}
              >
                Share Article
              </MenuItem>
            </Menu>
          </div>
        </Grid>

        {/* Right Panel - Chat Interface */}
        <Grid item xs={12} md={8} style={{ height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <div className="modern-card" style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }} data-tutorial="chat-interface">
            {selectedUser ? (
              <>
                {/* Header */}
                <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
                  <Typography variant="h6" className="text-neon" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ChatIcon style={{ fontSize: '1.2rem', color: '#6c3082' }} />
                    Chat with {selectedUser.name}
                  </Typography>
                  {console.log('🔍 Selected user:', selectedUser)}
                </Box>

                {/* Search Bar */}
                <Box sx={{ mb: 2, flexShrink: 0 }}>
                  <TextField
                    size="small"
                    placeholder="Search conversation..."
                    value={conversationSearch}
                    onChange={(e) => setConversationSearch(e.target.value)}
                    data-tutorial="conversation-search-bar"
                    InputProps={{
                      startAdornment: <SearchIcon sx={{ color: 'var(--text-secondary)', mr: 1 }} />
                    }}
                    sx={{ 
                      width: '100%',
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 'var(--radius-md)',
                        backgroundColor: 'var(--surface-2)',
                        '& fieldset': {
                          borderColor: 'var(--glass-border)',
                        },
                        '&:hover fieldset': {
                          borderColor: 'var(--neon-cyan)',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: 'var(--neon-cyan)',
                        },
                      }
                    }}
                  />
                </Box>

                {/* Messages Container */}
                <Box sx={{ 
                  flex: 1, 
                  overflow: 'auto', 
                  minHeight: 0,
                  p: 1,
                  '&::-webkit-scrollbar': {
                    width: '6px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: 'transparent',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: 'var(--glass-border)',
                    borderRadius: '3px',
                  },
                  '&::-webkit-scrollbar-thumb:hover': {
                    background: 'var(--neon-cyan)',
                  },
                }}>
                  {filteredMessages.map((message) => (
                    <ListItem
                      key={message.id}
                      style={{
                        padding: '1.5rem 0',
                        flexDirection: 'column',
                        alignItems: message.sender_id === 'current_user' ? 'flex-end' : 'flex-start'
                      }}
                    >
                      <div style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        maxWidth: '80%',
                        gap: '0.75rem'
                      }}>
                        {message.sender_id !== 'current_user' && (
                          <Avatar style={{ 
                            background: 'linear-gradient(135deg, #468230, #5cb85c)',
                            width: '32px',
                            height: '32px'
                          }}>
                            <PersonIcon fontSize="small" style={{ color: '#ffffff' }} />
                          </Avatar>
                        )}
                        
                        <div className="modern-card" style={{
                          background: message.sender_id === 'current_user' 
                            ? 'rgba(108, 48, 130, 0.15)' 
                            : 'rgba(70, 130, 48, 0.15)',
                          border: message.sender_id === 'current_user'
                            ? '1px solid rgba(108, 48, 130, 0.3)'
                            : '1px solid rgba(70, 130, 48, 0.3)',
                          padding: '1rem',
                          position: 'relative',
                          overflow: 'hidden'
                        }}>
                          <Typography variant="body1" style={{ 
                            color: 'var(--text-primary)',
                            whiteSpace: 'pre-wrap',
                            lineHeight: 1.6
                          }}>
                            {message.content}
                          </Typography>
                          
                          {message.shared_items && message.shared_items.map((item, index) => (
                            <Box key={index} sx={{ mt: 2 }}>
                              {renderSharedItem(item, message)}
                            </Box>
                          ))}
                          
                          <Typography variant="caption" style={{ 
                            color: 'var(--text-muted)',
                            display: 'block',
                            marginTop: '0.5rem',
                            opacity: 0.7
                          }}>
                            {message.sender_id === 'current_user' ? 'You' : selectedUser?.name} • {new Date(message.timestamp).toLocaleTimeString()}
                          </Typography>
                        </div>

                        {message.sender_id === 'current_user' && (
                          <Avatar style={{ 
                            background: 'linear-gradient(135deg, #6c3082, #8e44ad)',
                            width: '32px',
                            height: '32px'
                          }}>
                            <PersonIcon fontSize="small" style={{ color: '#ffffff' }} />
                          </Avatar>
                        )}
                      </div>
                    </ListItem>
                  ))}
                  <div ref={messagesEndRef} />
                </Box>

                {/* Message Input */}
                <div style={{ 
                  display: 'flex', 
                  gap: '1rem',
                  alignItems: 'flex-end',
                  flexShrink: 0
                }}>
                  <TextField
                    fullWidth
                    multiline
                    maxRows={4}
                    placeholder="Type your message..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        sendMessage();
                      }
                    }}
                    variant="outlined"
                    data-tutorial="message-input"
                    InputProps={{
                      startAdornment: (
                        <Tooltip title="Attach">
                          <IconButton
                            onClick={handleAttachmentMenuOpen}
                            data-tutorial="attachment-button"
                            sx={{
                              padding: '4px',
                              marginRight: '4px',
                              minWidth: '24px',
                              width: '24px',
                              height: '24px',
                              color: 'var(--text-muted)',
                              '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                color: 'var(--neon-cyan)',
                              },
                            }}
                          >
                            <AddIcon sx={{ fontSize: '16px' }} />
                          </IconButton>
                        </Tooltip>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        background: 'var(--surface-2)',
                        border: '1px solid var(--glass-border)',
                        borderRadius: 'var(--radius-lg)',
                        color: 'var(--text-primary)',
                        '& fieldset': {
                          borderColor: 'var(--glass-border)',
                        },
                        '&:hover fieldset': {
                          borderColor: 'rgba(108, 48, 130, 0.5)',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#6c3082',
                          borderWidth: '2px',
                        },
                      },
                      '& .MuiInputBase-input': {
                        color: 'var(--text-primary)',
                      },
                      '& .MuiInputBase-input::placeholder': {
                        color: 'var(--text-muted)',
                        opacity: 1,
                      },
                    }}
                  />
                  <Button
                    onClick={sendMessage}
                    disabled={!newMessage.trim() || sending}
                    className="btn-modern"
                    sx={{
                      ...getButtonStyles(false, isDarkMode, 'generic'),
                      minWidth: 'auto',
                      padding: '0.75rem',
                      height: '56px'
                    }}
                  >
                    {sending ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                  </Button>
                </div>
              </>
            ) : (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center',
                height: '100%',
                color: 'var(--text-secondary)',
                p: 4
              }}>
                <GroupIcon sx={{ 
                  fontSize: '4rem', 
                  mb: 2, 
                  opacity: 0.5,
                  color: 'var(--neon-cyan)'
                }} />
                <Typography variant="h6" sx={{ 
                  mb: 1,
                  color: 'var(--text-primary)',
                  fontWeight: '600'
                }}>
                  Select a colleague to start collaborating
                </Typography>
                <Typography variant="body2" sx={{
                  color: 'var(--text-secondary)',
                  lineHeight: 1.6,
                  textAlign: 'center'
                }}>
                  Share AI conversations, research articles, and findings with other researchers
                </Typography>
              </Box>
            )}
          </div>
        </Grid>
      </Grid>

      {/* Share Dialog */}
      <Dialog 
        open={shareDialogOpen} 
        onClose={() => {
          console.log('🔍 Closing share dialog');
          setShareDialogOpen(false);
        }}
        maxWidth="md"
        fullWidth
        data-tutorial="share-article-dialog"
        PaperProps={{
          sx: {
            backgroundColor: 'var(--surface-2)',
            color: 'var(--text-primary)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-md)'
          }
        }}
      >
        <DialogTitle sx={{ 
          color: 'var(--text-primary)',
          borderBottom: '1px solid var(--border)',
          pb: 2,
          mb: 2
        }}>
          Share {shareType.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()).replace(/\bAi\b/g, 'AI')}
        </DialogTitle>
        <DialogContent sx={{ 
          color: 'var(--text-primary)',
          backgroundColor: 'var(--surface-2)'
        }}>
          <Box sx={{ mb: 2 }}>
            {shareType === 'article' ? (
              <Box 
                sx={{ maxHeight: '400px', overflow: 'auto', mb: 2 }}
                data-tutorial="article-list-container"
              >
                {shareableItems && shareableItems.length > 0 ? (
                  shareableItems.map((queryGroup, queryIndex) => {
                    // Add safety checks for the data structure
                    const articles = queryGroup?.articles || [];
                    const queryTitle = queryGroup?.query_title || 'Unknown Query';
                    
                    return (
                      <Accordion 
                        key={queryIndex} 
                        sx={{ mb: 1, bgcolor: 'var(--surface-2)', border: '1px solid var(--glass-border)' }}
                        data-tutorial="share-article-accordion"
                      >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Typography variant="h6" sx={{ color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 1 }}>
                            <MenuBookIcon style={{ fontSize: '1.2rem', color: '#6c3082' }} />
                            {queryTitle} ({articles.length} articles)
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {articles.length > 0 ? (
                              articles.map((article, articleIndex) => (
                                <Box 
                                  key={`${queryIndex}-${articleIndex}-${article.id}`} 
                                  sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: 1,
                                    cursor: 'pointer',
                                    padding: '8px',
                                    borderRadius: 'var(--radius-sm)',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                      backgroundColor: 'rgba(108, 48, 130, 0.1)',
                                      transform: 'translateX(4px)'
                                    }
                                  }}
                                  data-tutorial="share-article-item"
                                  onClick={() => {
                                    const isCurrentlySelected = selectedItems.includes(article.id);
                                    console.log('🔍 Article selection:', {
                                      articleId: article.id,
                                      articleTitle: article.title,
                                      wasSelected: isCurrentlySelected,
                                      willBeSelected: !isCurrentlySelected,
                                      currentSelectedItems: selectedItems
                                    });
                                    
                                    if (isCurrentlySelected) {
                                      setSelectedItems(prev => {
                                        const newItems = prev.filter(id => id !== article.id);
                                        console.log('🔍 Removing article, new selected items:', newItems);
                                        return newItems;
                                      });
                                    } else {
                                      setSelectedItems(prev => {
                                        const newItems = [...prev, article.id];
                                        console.log('🔍 Adding article, new selected items:', newItems);
                                        return newItems;
                                      });
                                    }
                                  }}
                                >
                                  <Checkbox 
                                    checked={selectedItems.includes(article.id)}
                                    data-tutorial="share-article-checkbox"
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={(e) => {
                                      const isChecked = e.target.checked;
                                      if (isChecked) {
                                        setSelectedItems(prev => [...prev, article.id]);
                                      } else {
                                        setSelectedItems(prev => prev.filter(id => id !== article.id));
                                      }
                                    }}
                                    icon={<Box sx={{ 
                                      width: 20, 
                                      height: 20, 
                                      borderRadius: '50%', 
                                      border: '2px solid var(--text-muted)',
                                      backgroundColor: 'transparent'
                                    }} />}
                                    checkedIcon={<Box sx={{ 
                                      width: 20, 
                                      height: 20, 
                                      borderRadius: '50%', 
                                      border: '2px solid #6c3082',
                                      backgroundColor: '#6c3082',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      color: '#ffffff',
                                      fontSize: '14px',
                                      fontWeight: 'bold'
                                    }}>✓</Box>}
                                  />
                                  <Box sx={{ flex: 1 }}>
                                    <Typography variant="body2" sx={{ color: 'var(--text-primary)', fontWeight: 'bold' }}>
                                      {article.title}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: 'var(--text-muted)' }}>
                                      {article.description}
                                    </Typography>
                                  </Box>
                                </Box>
                              ))
                            ) : (
                          <Typography variant="body2" sx={{ color: 'var(--text-muted)', textAlign: 'center', py: 2 }}>
                            No articles found in this query
                          </Typography>
                        )}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                    );
                  })
                ) : (
                  <Typography variant="body2" sx={{ color: 'var(--text-muted)', textAlign: 'center', py: 4 }}>
                    No articles available to share
                  </Typography>
                )}
              </Box>
            ) : (
              <Box 
                sx={{ maxHeight: '400px', overflow: 'auto', mb: 2 }}
                data-tutorial="conversation-list-container"
              >
                {shareableItems && shareableItems.length > 0 ? (
                  shareableItems.map((conversation, index) => {
                    const conversationId = conversation.id || conversation._id || `conv-${index}`;
                    const conversationTitle = conversation.title || conversation.preview || 'Untitled Conversation';
                    const conversationPreview = conversation.preview || conversation.title || '';
                    const messageCount = conversation.message_count || conversation.messages?.length || 0;
                    const conversationDate = conversation.date || conversation.created_at || conversation.createdAt || '';
                    
                    return (
                      <Box 
                        key={conversationId}
                        sx={{ 
                          mb: 1, 
                          bgcolor: 'var(--surface-2)', 
                          border: '1px solid var(--glass-border)',
                          borderRadius: 'var(--radius-md)',
                          overflow: 'hidden'
                        }}
                        data-tutorial="share-conversation-item"
                      >
                        <Box
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1,
                            cursor: 'pointer',
                            padding: '12px',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              backgroundColor: 'rgba(108, 48, 130, 0.1)',
                            }
                          }}
                          onClick={() => {
                            const isCurrentlySelected = selectedItems.includes(conversationId);
                            if (isCurrentlySelected) {
                              setSelectedItems(prev => prev.filter(id => id !== conversationId));
                            } else {
                              setSelectedItems(prev => [...prev, conversationId]);
                            }
                          }}
                        >
                          <Checkbox 
                            checked={selectedItems.includes(conversationId)}
                            data-tutorial="share-conversation-checkbox"
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => {
                              const isChecked = e.target.checked;
                              if (isChecked) {
                                setSelectedItems(prev => [...prev, conversationId]);
                              } else {
                                setSelectedItems(prev => prev.filter(id => id !== conversationId));
                              }
                            }}
                            icon={<Box sx={{ 
                              width: 20, 
                              height: 20, 
                              borderRadius: '50%', 
                              border: '2px solid var(--text-muted)',
                              backgroundColor: 'transparent'
                            }} />}
                            checkedIcon={<Box sx={{ 
                              width: 20, 
                              height: 20, 
                              borderRadius: '50%', 
                              border: '2px solid #6c3082',
                              backgroundColor: '#6c3082',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: '#ffffff',
                              fontSize: '14px',
                              fontWeight: 'bold'
                            }}>✓</Box>}
                          />
                          <PsychologyIcon sx={{ fontSize: '1.2rem', color: '#6c3082', flexShrink: 0 }} />
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" sx={{ color: 'var(--text-primary)', fontWeight: 'bold' }}>
                              {conversationTitle}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                              {messageCount > 0 && (
                                <Chip 
                                  label={`${messageCount} message${messageCount !== 1 ? 's' : ''}`}
                                  size="small"
                                  sx={{ 
                                    height: '18px',
                                    fontSize: '0.65rem',
                                    backgroundColor: 'rgba(108, 48, 130, 0.1)',
                                    color: '#6c3082'
                                  }}
                                />
                              )}
                              {conversationPreview && (
                                <span>{conversationPreview.length > 100 ? conversationPreview.substring(0, 100) + '...' : conversationPreview}</span>
                              )}
                              {conversationDate && (
                                <span> • {new Date(conversationDate).toLocaleDateString()}</span>
                              )}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    );
                  })
                ) : (
                  <Typography variant="body2" sx={{ color: 'var(--text-muted)', textAlign: 'center', py: 4 }}>
                    No AI conversations available to share
                  </Typography>
                )}
              </Box>
            )}
            
            <TextField
              fullWidth
              multiline
              rows={3}
              placeholder="Add a message (optional)..."
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              data-tutorial="share-message-input"
              className="input-modern"
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: 'var(--text-primary)',
                  backgroundColor: 'var(--surface-2)',
                  '& fieldset': {
                    borderColor: 'var(--glass-border)',
                  },
                  '&:hover fieldset': {
                    borderColor: '#6c3082',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#6c3082',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: 'var(--text-muted)',
                  '&.Mui-focused': {
                    color: '#6c3082'
                  }
                },
                '& .MuiInputBase-input::placeholder': {
                  color: 'var(--text-muted)',
                  opacity: 0.7
                }
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ 
          borderTop: '1px solid var(--border)',
          padding: '16px 24px',
          gap: 1
        }}>
          <Button 
            onClick={() => setShareDialogOpen(false)}
            className="btn-modern"
            sx={getButtonStyles(false, isDarkMode, 'generic')}
          >
            Cancel
          </Button>
          <Button 
            onClick={shareItems}
            disabled={selectedItems.length === 0 || sending}
            className="btn-modern"
            data-tutorial="share-submit-button"
            sx={getButtonStyles(false, isDarkMode, 'generic')}
          >
            {sending ? <CircularProgress size={20} color="inherit" /> : 'Share'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* New Chat Dialog */}
      <Dialog
        open={newChatDialogOpen}
        onClose={() => setNewChatDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: 'var(--surface-2)',
            border: '1px solid var(--glass-border)',
            borderRadius: 'var(--radius-lg)',
            backdropFilter: 'blur(10px)',
          }
        }}
      >
        <DialogTitle sx={{ color: 'var(--text-primary)', borderBottom: '1px solid var(--glass-border)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AddIcon sx={{ color: 'var(--neon-cyan)' }} />
            Start New Chat
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ p: 3 }}>
          <Typography variant="body2" sx={{ color: 'var(--text-secondary)', mb: 2 }}>
            Search for a colleague to start a new conversation
          </Typography>
          
          <TextField
            fullWidth
            placeholder="Search by name, email, role, or institution..."
            value={colleagueSearchQuery}
            onChange={handleColleagueSearchChange}
            data-tutorial="user-search-input"
            InputProps={{
              startAdornment: <SearchIcon sx={{ color: 'var(--text-secondary)', mr: 1 }} />
            }}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: 'var(--radius-md)',
                backgroundColor: 'var(--surface-2)',
                '& fieldset': {
                  borderColor: 'var(--glass-border)',
                },
                '&:hover fieldset': {
                  borderColor: 'var(--neon-cyan)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'var(--neon-cyan)',
                },
              }
            }}
          />

          {/* Search Results */}
          <Box sx={{ maxHeight: '300px', overflow: 'auto' }}>
            {searchingColleagues ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : searchResults.length > 0 ? (
              <List dense>
                {searchResults.map((user) => (
                  <ListItem
                    key={user.id}
                    className="modern-card"
                    data-tutorial="user-search-result"
                    sx={{
                      py: 1,
                      mb: 1,
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        transform: 'translateY(-1px)'
                      },
                      transition: 'all 0.2s ease-in-out'
                    }}
                    onClick={() => handleStartChat(user)}
                  >
                    <ListItemIcon sx={{ minWidth: 48 }}>
                      <Avatar sx={{
                        bgcolor: 'var(--neon-cyan)',
                        color: 'white',
                        width: 32,
                        height: 32,
                        fontSize: '0.875rem'
                      }}>
                        {user.name.charAt(0).toUpperCase()}
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body2" style={{ color: 'var(--text-primary)' }}>
                          {user.name}
                        </Typography>
                      }
                      secondary={
                        <div>
                          <Typography variant="caption" style={{ color: 'var(--text-muted)' }}>
                            {user.email}
                          </Typography>
                          <br />
                          <Typography variant="caption" style={{ color: 'var(--text-muted)' }}>
                            {user.role} • {user.institution}
                          </Typography>
                        </div>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            ) : colleagueSearchQuery.trim() ? (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="body2" sx={{ color: 'var(--text-muted)' }}>
                  No colleagues found matching "{colleagueSearchQuery}"
                </Typography>
              </Box>
            ) : (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="body2" sx={{ color: 'var(--text-muted)' }}>
                  Start typing to search for colleagues
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, borderTop: '1px solid var(--glass-border)' }}>
          <Button
            onClick={() => setNewChatDialogOpen(false)}
            className="btn-modern"
            sx={getButtonStyles(false, isDarkMode, 'generic')}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Article Details Modal - Resizable */}
      {articleModalOpen && selectedArticle && (
        <Box
          data-tutorial="article-detail-modal"
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(5px)',
            zIndex: 1300,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 2
          }}
          onClick={handleCloseArticleModal}
        >
          <Box
            data-tutorial="article-detail-window"
            sx={{
              width: `${dialogWidth}px`,
              maxWidth: '80vw',
              maxHeight: '80vh',
              background: 'var(--surface-2)',
              border: '1px solid var(--glass-border)',
              borderRadius: 'var(--radius-lg)',
              backdropFilter: 'blur(10px)',
              transform: 'translateZ(0)',
              willChange: 'transform',
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
              overflow: 'hidden'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Resize Handle */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                right: 0,
                bottom: 0,
                width: '8px',
                cursor: 'col-resize',
                backgroundColor: 'transparent',
                zIndex: 1,
                '&:hover': {
                  backgroundColor: 'rgba(108, 48, 130, 0.2)',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: '50%',
                    right: '2px',
                    transform: 'translateY(-50%)',
                    width: '2px',
                    height: '20px',
                    backgroundColor: 'var(--neon-cyan)',
                    borderRadius: '1px'
                  }
                }
              }}
              onMouseDown={handleMouseDown}
            />
        {selectedArticle && (
          <>
            <DialogTitle sx={{ 
              color: 'var(--text-primary)',
              borderBottom: '1px solid var(--glass-border)',
              pb: 2,
              transform: 'translateZ(0)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <ArticleIcon style={{ color: 'var(--success)', fontSize: '1.2rem' }} />
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  Article Details
                </Typography>
              </Box>
              <Typography variant="caption" sx={{ 
                color: 'var(--text-muted)',
                fontSize: '0.75rem',
                opacity: 0.7
              }}>
                {dialogWidth}px
              </Typography>
              <Typography variant="h5" sx={{ color: 'var(--text-primary)', lineHeight: 1.3 }}>
                {selectedArticle.title}
              </Typography>
            </DialogTitle>
            
            <DialogContent sx={{ pt: 2, transform: 'translateZ(0)' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* Authors */}
                <Box>
                  <Typography variant="subtitle2" sx={{ color: 'var(--success)', mb: 0.5 }}>
                    Authors
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'var(--text-primary)' }}>
                    {selectedArticle.authors}
                  </Typography>
                </Box>

                {/* Journal and Year */}
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Box>
                    <Typography variant="subtitle2" sx={{ color: 'var(--success)', mb: 0.5 }}>
                      Journal
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'var(--text-primary)' }}>
                      {selectedArticle.journal}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" sx={{ color: 'var(--success)', mb: 0.5 }}>
                      Year
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'var(--text-primary)' }}>
                      {selectedArticle.year}
                    </Typography>
                  </Box>
                </Box>

                {/* Abstract (Read-only) */}
                {selectedArticle.abstract && (
                  <Box>
                    <Typography variant="subtitle2" sx={{ color: 'var(--success)', mb: 0.5 }}>
                      Abstract
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: 'var(--text-primary)',
                        lineHeight: 1.6,
                        textAlign: 'justify',
                        whiteSpace: 'pre-wrap'
                      }}
                    >
                      {selectedArticle.abstract}
                    </Typography>
                  </Box>
                )}

                {/* Description */}
                {selectedArticle.description && selectedArticle.description !== selectedArticle.abstract && (
                  <Box>
                    <Typography variant="subtitle2" sx={{ color: 'var(--success)', mb: 0.5 }}>
                      Description
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: 'var(--text-primary)',
                        lineHeight: 1.6,
                        textAlign: 'justify'
                      }}
                    >
                      {selectedArticle.description}
                    </Typography>
                  </Box>
                )}

                {/* Shared Status */}
                <Box>
                  <Typography variant="subtitle2" sx={{ color: 'var(--success)', mb: 0.5 }}>
                    Status
                  </Typography>
                  <Chip
                    label="Shared Article"
                    size="small"
                    sx={{
                      backgroundColor: 'rgba(70, 130, 48, 0.2)',
                      color: 'var(--success)',
                      border: '1px solid var(--success)',
                    }}
                  />
                </Box>
              </Box>
            </DialogContent>
            
            <DialogActions sx={{ 
              borderTop: '1px solid var(--glass-border)',
              pt: 2,
              px: 3,
              pb: 3,
              display: 'flex',
              justifyContent: 'space-between'
            }}>
              <Box sx={{ flex: 1 }}>
                {selectedArticle?.url && (
                  <Button 
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      console.log('🔍 Opening URL:', selectedArticle.url);
                      // Safari-compatible approach
                      const link = document.createElement('a');
                      link.href = selectedArticle.url;
                      link.target = '_blank';
                      link.rel = 'noopener noreferrer';
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                    }}
                    className="btn-modern"
                    startIcon={<OpenInNewIcon />}
                    sx={getButtonStyles(false, isDarkMode, 'generic')}
                  >
                    Open Article
                  </Button>
                )}
              </Box>
              
              <Button 
                onClick={handleCloseArticleModal}
                className="btn-modern"
                sx={getButtonStyles(false, isDarkMode, 'generic')}
              >
                Close
              </Button>
            </DialogActions>
          </>
        )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default Collaboration;